/**
 * Tipos del WhatsApp oficial (Twilio): espejo del contrato del backend
 * (`backend/src/modules/wa-oficial/types` y SPEC-backend §13). Si el backend cambia un campo, se
 * cambia acá. Las fechas llegan como string ISO.
 *
 * Por qué un archivo propio y no `campana-nueva/types.real.ts`: aquel espeja Activa, y `ChipEstado`
 * hace `Record<EstadoCampana>` sobre él. Mezclar los estados del oficial (`borrador`,
 * `pausada_auto`…) con los de Activa (`draft`, `sent`…) invita a importar el equivocado.
 *
 * No hay `CONFIG_POR_DEFECTO` en el front: `GET /cuenta` siempre devuelve una `CuentaPublica` con
 * `config` (la virtual trae los defaults del backend). Una sola fuente de verdad.
 */

import type { RespuestaRapida, ResumenConversaciones, TipoEntrante } from "./types.conversaciones";
import type { DisparadorDeLaCampana } from "./types.disparadores";

export type ModoTransporte = "simulated" | "twilio";
/**
 * `whatsapp/card` (decisión D-A): la promo con imagen o con link. Cuerpo hasta 1024, encabezado
 * opcional (imagen `mediaUrl` o texto `encabezadoTexto`, nunca los dos), pie en `footer` (no dentro del
 * cuerpo) y botones QUICK_REPLY (≤ 25) y URL (≤ 20, hasta 2).
 */
export type TipoPlantilla = "twilio/text" | "twilio/media" | "twilio/quick-reply" | "twilio/call-to-action" | "whatsapp/card";
export type Categoria = "MARKETING" | "UTILITY";
export type CategoriaCobro = Categoria | "AUTHENTICATION" | "SERVICIO";
export type Proposito = "promocion" | "aviso" | "beneficio";
export type EstadoPlantilla = "borrador" | "unsubmitted" | "received" | "pending" | "approved"
  | "rejected" | "paused" | "disabled" | "eliminada";

export interface BotonPlantilla {
  tipo: "QUICK_REPLY" | "URL" | "PHONE_NUMBER";
  titulo: string; id?: string; url?: string; telefono?: string;
  /** un botón que cuenta los clics (`logic/clics`): a dónde lleva de verdad; `url` es el link de Deenex con la variable */
  destino?: string;
}
export type CampoVariableFijo = "nombre" | "primerNombre" | "apellido" | "grupo" | "oferta" | "vence" | "sucursal" | "marca" | "cupon";
/**
 * Un campo personalizado de la base de datos va como "campo:<clave>" (igual que el backend). Sólo existe
 * con audiencia "base": qué campos valen para una campaña lo dice `GET /campos?audiencia=`, no este tipo.
 */
export type CampoVariable = CampoVariableFijo | `campo:${string}`;
export interface VariablePlantilla { numero: number; alias: string; ejemplo: string; campoSugerido?: CampoVariable }
export interface PlantillaBorrador {
  nombreVisible: string; proposito: Proposito; categoria: Categoria; tipo: TipoPlantilla;
  idioma: string; cuerpo: string; pie?: string; mediaUrl?: string;
  /** Sólo `whatsapp/card`: encabezado de texto (≤ 60, sin variables). Nunca junto con `mediaUrl` (D-A). Mismo nombre que el backend. */
  encabezadoTexto?: string;
  botones: BotonPlantilla[]; variables: VariablePlantilla[];
}
export interface ModeloPlantilla extends PlantillaBorrador { clave: string; descripcion: string }

export interface ReglaResultado { id: string; nivel: "error" | "advertencia"; ok: boolean; mensaje: string; campo?: string }
export interface ResultadoValidacionApi {
  ok: boolean; reglas: ReglaResultado[]; cuerpoCompuesto: string;
  variablesDetectadas: number[];   // incluye las de URLs de botones y mediaUrl (SPEC-backend §6.5)
  nombreTecnicoSugerido: string; previa: string;
  costoPorMensaje: { categoria: Categoria; usd: number; clienteUSD: number; tarifaVigencia: string };
}

export interface HistorialPlantilla { estado: EstadoPlantilla; categoria?: string; at: string; fuente: "api" | "evento" | "polling" | "simulado" | "envio"; detalle?: string }
export interface PlantillaResumen {
  id: string; nombreVisible: string; nombreTecnico: string; version: number; tipo: TipoPlantilla;
  categoria: Categoria | "AUTHENTICATION"; categoriaSolicitada: Categoria; estado: EstadoPlantilla; archivada: boolean;
  rejectionReason?: string; recategorizada: { de: string; a: string; at: string } | null;
  enviadaAAprobacionAt?: string | null; aprobadaAt?: string | null; horasEnRevision?: number | null;
  /** `costoPorMensajeUSD` es lo que le cuesta a Deenex: no le llega a una persona de un cliente. */
  costoPorMensajeUSD?: number; costoPorMensajeClienteUSD: number; usadaEnCampanas: number;
  /**
   * Opcionales porque el backend los suma en esta etapa (A1-4) y hay tests que arman un resumen
   * sin ellos. Ausente = no se sabe: la pantalla no inventa un chip, y el backend igual rechaza la
   * campaña real con `PLANTILLA_SIMULADA` o `PLANTILLA_DE_OTRA_CUENTA` (la última palabra es suya).
   */
  simulado?: boolean;        // se aprobó en modo simulado: el HX no existe en Twilio
  deOtraCuenta?: boolean;    // se creó con otra cuenta de Twilio (otro Account SID)
}
export interface PlantillaDetalle extends PlantillaResumen {
  proposito: Proposito; idioma: string; cuerpo: string; pie?: string; mediaUrl?: string; encabezadoTexto?: string;
  botones: BotonPlantilla[]; variables: VariablePlantilla[]; cuerpoCompuesto: string; contentSid?: string;
  historial: HistorialPlantilla[]; validacion?: { ok: boolean; reglas: ReglaResultado[] } | null;
  campanasQueLaUsan: { id: string; nombre: string; estado: EstadoCampanaOficial }[];
  metricas: { entregados: number; leidos: number; bajas: number };
  versionAnteriorId?: string | null; creadoPor?: Quien; enviadaPor?: Quien; simulado?: boolean;
}
export interface ListaPlantillas { items: PlantillaResumen[]; cupo: { usadas: number; limite: number } }
export interface Quien { email: string; ip?: string }

export type MapeoVariable =
  | { fuente: "campo"; campo: CampoVariable; siVacio?: { accion: "default"; valor: string } | { accion: "excluir" } | { accion: "vacio" } }
  | { fuente: "fijo"; valor: string };
export type MapeoVariables = Record<string, MapeoVariable>;
export interface CampoDisponible { campo: CampoVariable; etiqueta: string; ejemplo: string }

export type EstadoCampanaOficial = "borrador" | "programada" | "enviando" | "pausada" | "pausada_auto" | "finalizada" | "cancelada";
/** «calidad_media» y «calidad_baja»: el freno por calidad del número (se abren solas, como la franja y el cupo). */
export type EsperaCampana = null | "fuera_de_franja" | "cupo_24h" | "calidad_media" | "calidad_baja" | "saldo" | "tope_mensual" | "marca_frenada" | "reintentos" | "tope_diario";
export type EstadoMensajeOficial = "control" | "pendiente" | "reservado" | "desconocido" | "aceptado" | "enviado"
  | "entregado" | "leido" | "fallido" | "no_entregado" | "excluido" | "cancelado" | "sin_confirmacion";
/**
 * «filtro_compras» (28/10/2026): no cumple el filtro «Por compras» de la audiencia; va primero, como en el backend.
 * «en_lista_exclusion» y «recibio_otra_campana» (28/10/2026): las exclusiones de la audiencia, después del filtro.
 */
export type MotivoExclusion = "filtro_compras" | "en_lista_exclusion" | "recibio_otra_campana" | "sin_celular" | "telefono_invalido" | "numero_eeuu" | "telefono_ambiguo" | "numero_invalido_previo"
  | "baja_marketing" | "no_contactar" | "sin_optin" | "limitado_meta_24h" | "fatiga" | "duplicado" | "variable_vacia";
/** Mismo orden que ORDEN_GATES del backend (§6.14): es el orden en que se muestran. */
export const ORDEN_GATES: readonly MotivoExclusion[] = ["filtro_compras", "en_lista_exclusion", "recibio_otra_campana", "sin_celular", "telefono_invalido", "numero_eeuu", "telefono_ambiguo", "numero_invalido_previo",
  "baja_marketing", "no_contactar", "sin_optin", "limitado_meta_24h", "fatiga", "duplicado", "variable_vacia"];

export interface FranjaHoraria { desde: string; hasta: string; dias?: number[] }
/**
 * El horario propio de una campaña (todo opcional; null = el de la marca). Las franjas valen sólo dentro de las de la
 * marca; `topeDiario` es por día de la marca y no pasa el cupo de Meta; `hasta` ("AAAA-MM-DD") es el último día en que sale.
 * `zona`: la de la campaña (`logic/zonas`), sólo si no es la de la marca.
 */
export interface HorarioCampana { franjas: FranjaHoraria[] | null; topeDiario: number | null; hasta: string | null; zona?: string | null }
export interface ConfigCuenta {
  // Sin `controlPct`: WhatsApp oficial no tiene grupo de control (el backend rechaza un valor > 0 con 422 CONTROL_NO_DISPONIBLE).
  /** `recargoPct` (el recargo de Deenex) no le llega a una persona de un cliente: el backend lo saca de su respuesta. */
  ritmoMps: number; cupoManual24h: number | null; fatigaDias: number; recargoPct?: number;
  /**
   * La regla de fatiga de la marca (29/10/2026): «como mucho `fatigaMaximo` mensajes cada `fatigaDias` días por persona,
   * sumando WhatsApp oficial y por QR»; con `fatigaContarMails`, también los mails que se pueden contar por persona.
   * Opcionales: un backend anterior no los manda (y entonces no se ofrecen).
   */
  fatigaMaximo?: number;
  fatigaContarMails?: boolean;
  /** Hasta cuántos días desde la última compra un contacto es «caliente» y sale primero (0 = sólo los que escribieron).
   *  Opcional: un backend anterior no lo manda (y entonces no se ofrece). */
  diasCaliente?: number;
  /** Freno por calidad: media = mitad del cupo; roja = marketing en pausa 7 días. Opcional, como `diasCaliente`. */
  frenoPorCalidad?: boolean;
  /** El texto del QR de Calentamiento que vale como consentimiento («» = el sugerido). Opcional, como `diasCaliente`. */
  textoQr?: string;
  topeMensualUSD: number | null; umbralSaldoBajoUSD: number; franjas: FranjaHoraria[]; franjasTexto?: string;
  /** el comienzo del link propio que cuenta los clics de un botón (lo arma el backend); null sin link público */
  prefijoClics?: string | null;
  textoBaja: string; botonBaja: string; confirmacionBaja: string;
  respuestaAutomatica: { activa: boolean; texto: string }; excluirTelefonosAmbiguos: boolean;
  advancedOptOutConfirmado: boolean; idioma: string;
  /**
   * Los tres son OPCIONALES aunque el backend nuevo siempre los manda: `CampanaAsistentePage.test`
   * y `PlantillaEditorPage.test` arman un `ConfigCuenta` completo sin ellos, y un backend viejo no
   * los trae. La UI los lee SIEMPRE con los helpers de `logic/config.ts`, que ponen el default.
   * `alertasTwilio` no viaja en el PATCH de config: se cambia desde Conexión (técnico) (su endpoint).
   */
  alertasTwilio?: AlertasTwilioConfig;
  linkShortening?: LinkShorteningConfig;
  complianceConfirmado?: boolean;
  /**
   * Conversaciones (spec de conversaciones §2.6). Opcionales por la misma razón que los tres de arriba: los
   * fixtures tipados de otros tests no los traen, y un backend anterior tampoco. "" = sin aviso por mail.
   */
  mailAvisos?: string;
  respuestasRapidas?: RespuestaRapida[];
}
/** Alertas de gasto de Twilio (Usage Triggers, horario UTC). null = sin alerta. */
export interface AlertasTwilioConfig { gastoDiarioUSD: number | null; gastoMensualUSD: number | null; saldoMinimoUSD: number }
/** Link Shortening: apagado por defecto (necesita dominio propio en Twilio y cuesta aparte). */
export interface LinkShorteningConfig { activo: boolean; enSesiones: boolean; enPlantillas: boolean }
export type EstadoCuenta = "sin_verificar" | "verificando" | "verificada" | "error";
export interface SenderInfo {
  estado: string;                                  // ONLINE, OFFLINE, … (Senders v2, abierto)
  calidad: "GREEN" | "YELLOW" | "RED" | "UNKNOWN" | string;
  messagingLimit: number | null;                   // null + messagingLimitIlimitado:false = Meta no dio un dato legible (se usa 250)
  messagingLimitIlimitado: boolean;                // true = sin límite (messagingLimit viene null)
  messagingLimitRaw?: string; nombreVisible?: string; enServicio?: boolean | null; actualizadoAt?: string;
  calidadRaw?: string;                             // lo que dijo Twilio ("HIGH", "MEDIUM"…) antes de normalizar
  offlineReasons?: { code: string; message: string; moreInfo: string }[];
  webhookCallbackUrl?: string;                     // webhook propio del número (Senders v2), si tiene
}
/**
 * El límite de Meta que se usa y de dónde sale (`GET /cuenta`): Twilio si lo informa; si no, el que el equipo leyó en
 * WhatsApp Manager para este número (`confirmado`); si no, el mínimo (`limite` = 250). Opcional: un backend anterior no lo manda.
 */
export interface LimiteMetaPublico {
  limite: number | null; ilimitado: boolean; fuente: "twilio" | "equipo" | "minimo";
  confirmado: { limite: number | null; ilimitado: boolean; at: string } | null;
}
/** Lo que acepta `limiteMetaConfirmado` en el PATCH de config (un nivel de Meta sobre el mínimo, o «ilimitado»); null lo borra. */
export type LimiteMetaElegible = 2000 | 10000 | 100000 | "ilimitado";
/** Nunca Infinity: limite es null SÓLO con ilimitado:true (SPEC-backend §6.13). */
export interface Cupo24h {
  limite: number | null; ilimitado: boolean; usados: number; disponibles: number | null;
  /** El freno por calidad achicó el cupo a la mitad («pausa»: además el marketing espera). Opcional: sólo en el Inicio. */
  frenoCalidad?: "mitad" | "pausa"; limiteMeta?: number | null;
}
export interface WebhooksCuenta {
  inboundConfigurado: boolean; inboundUrl?: string;
  eventStreams: "sin_configurar" | "pendiente_validacion" | "activo" | "error";
  ultimoStatusAt?: string | null; ultimoInboundAt?: string | null; ultimoEventoAt?: string | null; lastError?: string;
  pruebaSinkPedidaAt?: string | null;              // cuándo le pedimos a Twilio el evento de prueba del sink
  ultimoEventoFirmadoAt?: string | null;           // último POST de Twilio a /events con firma válida
  recibosAjenos?: number;                          // recibos de estado de mensajes que no son nuestros
}
export interface CuentaPublica {
  id: string | null;                               // null = cuenta virtual: la marca todavía no verificó ni cargó credenciales
  modoTransporte: ModoTransporte; modo: "twilio" | "simulado" | null; estado: EstadoCuenta;
  accountSidEnmascarado?: string; authTokenGuardado: boolean; messagingServiceSid?: string; senderE164?: string;
  sender?: SenderInfo; portfolioVerificado: boolean; verificadaAt?: string | null; lastError?: string;
  limiteMeta?: LimiteMetaPublico;
  webhooks: WebhooksCuenta; config: ConfigCuenta; ritmoActualMps: number; saldoUSD: number;
  gastoMes: { mes: string; usd: number };
  /**
   * `superadaPorPrueba`: el freno es por `cumplimiento` (un envío dio 20003 con el token bueno) y una prueba del
   * Sandbox salió después. La marca sigue frenada: la pantalla ofrece reanudarla. Opcional: un backend anterior no lo manda.
   */
  frenada: { activa: boolean; codigo?: string; motivo?: string; at?: string; superadaPorPrueba?: FrenoSuperado };
  publicBaseUrlConfigurada: boolean; marcaDemoBloqueada: boolean;
  /**
   * La cuenta se verificó en otro modo (en simulado, con el servidor en twilio): el backend la manda
   * como `sin_verificar` con este flag. Opcional: un backend anterior manda `verificada` con
   * `modo: "simulado"`, y `cuentaDelModo` (logic/conexion.ts) tampoco la da por conectada.
   */
  verificadaEnOtroModo?: boolean;
  /** Hay SMTP en el servidor: sin él, el aviso por mail de Conversaciones no sale. Opcional: un backend anterior no lo manda. */
  smtpConfigurado?: boolean;
  /** Datos de la cuenta de Twilio (§A.2.1). Opcional: un backend viejo no lo manda. */
  twilio?: {
    tipoCuenta: string; nombreCuenta: string; esSubcuenta: boolean | null;
    /** FriendlyName del Messaging Service conectado; vacío si todavía no se leyó. */
    nombreServicio?: string;
    saldo: { valor: number | null; moneda: string; at: string } | null;
  } | null;
}
/** Un freno por `cumplimiento` que una prueba del Sandbox posterior superó: qué freno (su `at`) y qué prueba. */
export interface FrenoSuperado { frenadaAt: string; pruebaAt: string; messageSid: string }
export interface CredencialesEntrada { accountSid: string; authToken: string; messagingServiceSid: string; senderE164: string }

export type TipoMovimiento = "recarga" | "ajuste" | "consumo" | "reintegro" | "consumo_entrante" | "consumo_sesion";
export interface Movimiento {
  id: string; tipo: TipoMovimiento; montoUSD: number; campanaId?: string; mensajeId?: string;
  /**
   * `motivo` y `conversacionId` los ponen el cupo de servicio y las respuestas y reaperturas de Conversaciones
   * (`cupo_servicio`, `respuesta_manual`…). Opcionales: los movimientos de campaña no los llevan.
   */
  detalle?: { categoria?: string; metaUSD?: number; twilioUSD?: number; recargoPct?: number; codigoError?: string; motivo?: string; conversacionId?: string };
  comprobante?: string; nota?: string; registradoPor?: Quien; createdAt: string; simulado: boolean;
  /** Simulado que el rebase del paso a real sacó del saldo. Opcional: un backend anterior no lo manda. */
  excluidoDelSaldo?: boolean;
}
export interface Saldo {
  saldoUSD: number; gastoMes: { mes: string; usd: number }; topeMensualUSD: number | null;
  alertas: ("saldo_bajo" | "tope_80" | "tope_alcanzado")[];
  movimientos: Movimiento[]; total: number; page: number; limit: number;
  /** La última auditoría `rebase_saldo` (paso a real), o null si no hubo. Opcional: un backend anterior no la manda. */
  rebase?: RebaseSaldo | null;
}
/** Qué sacó del saldo el paso a real: el saldo antes y después, y cuántos movimientos simulados quedaron fuera. */
export interface RebaseSaldo { at: string; antesUSD: number; despuesUSD: number; excluidos: number }
/** `?simulados=` de los movimientos del saldo: en twilio el backend excluye por defecto. */
export type FiltroSimulados = "incluir" | "excluir";
export interface MovimientoResultado { saldoUSD: number; movimiento: Movimiento }

export type CodigoAlertaResumen = "plantilla_pausada" | "plantilla_deshabilitada" | "plantilla_48h" | "recategorizada"
  | "sin_callbacks_1h" | "sender_inactivo_25d" | "saldo_bajo" | "tope_80" | "tope_alcanzado" | "marca_frenada"
  | "sender_offline" | "event_streams_error" | "advanced_opt_out"
  // Alertas de la cuenta de Twilio (SPEC-estadisticas-conexion §B.3.6)
  | "saldo_twilio_bajo" | "gasto_twilio_diario" | "gasto_twilio_mensual" | "calidad_media" | "calidad_baja"
  | "plantillas_otra_cuenta" | "pedido_de_alta" | "diagnostico_con_errores" | "sender_actualizando"
  // El saldo de la cuenta de Twilio no alcanza para lo que cargaron las marcas (la cobertura del saldo): del equipo
  | "cobertura_twilio"
  // La plantilla que se creó desde una campaña ya tiene respuesta de Meta (SPEC-BD)
  | "borrador_plantilla_aprobada" | "borrador_plantilla_rechazada"
  // Un borrador real apunta a una plantilla del modo simulado (o de otra cuenta): hay que cambiarla
  | "borrador_plantilla_simulada";
export interface EnlaceAlerta { tipo: "plantilla" | "campana" | "saldo" | "cuenta" | "config" | "bajas"; id?: string } // lista cerrada (SPEC-backend §13.1)
export interface AlertaResumen { codigo: CodigoAlertaResumen | string; nivel: "info" | "alerta" | "corte"; mensaje: string; enlace: EnlaceAlerta }
export interface Resumen {
  modoTransporte: ModoTransporte;
  cuenta: null | { estado: EstadoCuenta; modo: "twilio" | "simulado"; senderE164?: string; sender?: SenderInfo;
    webhooks: Partial<WebhooksCuenta>; frenada: { activa: boolean; codigo?: string; motivo?: string } };
  cupo24h: Cupo24h;
  saldo: { saldoUSD: number; gastoMesUSD: number; topeMensualUSD: number | null; simulado: boolean };
  /**
   * En twilio, `porEstado` y `cupo` ya vienen SIN las plantillas que no sirven con la cuenta (simuladas
   * o de otra cuenta): ésas llegan aparte, en `noUsables`. Opcional: un backend anterior no lo manda.
   */
  plantillas: { porEstado: Partial<Record<EstadoPlantilla, number>>; cupo: { usadas: number; limite: number }; noUsables?: PlantillasNoUsables };
  alertas: AlertaResumen[];
  /** `simulado` opcional: un backend anterior no lo manda (y entonces no se pinta el chip). */
  ultimasCampanas: { id: string; nombre: string; estado: EstadoCampanaOficial; espera: EsperaCampana; enviados: number; total: number; simulado?: boolean }[];
  /** Por contestar y sin responder de Conversaciones. Opcional: un backend anterior no lo manda; null si el conteo falló. */
  conversaciones?: ResumenConversaciones | null;
  /** ¿Ya hay una campaña confirmada con el modo de hoy? Cierra «Para empezar a enviar». Opcional: un backend anterior no
   *  lo manda; null si no se supo (en los dos casos el paso no se muestra). */
  hayCampanaConfirmada?: boolean | null;
}
export interface PlantillasNoUsables { simuladas: number; deOtraCuenta: number }

/** Calentamiento del número (`GET /calentamiento`, util/calentamiento.util.ts del backend). */
export type EstadoPasoCalentamiento = "ok" | "advertencia" | "error";
export interface PasoCalentamiento {
  id: "portfolio" | "calidad" | "perfil" | "plantillas" | "contactos" | "uso" | string;
  titulo: string; estado: EstadoPasoCalentamiento; detalle: string;
  /** Qué hacer, sólo si hay algo para hacer. */
  arreglo?: string;
  /** Todavía no se puede medir (la calidad, el perfil sin leer, el uso sin límite conocido): «se mide después», no
   *  «falta». Opcional: un backend anterior no lo manda (y esos pasos se ven como «falta»). */
  sinMedir?: boolean;
}
/** «verificacion» = esperar a Meta; «entregas» = 2.000 personas en 30 días; «uso» = la mitad del límite 7 días. */
export interface PasoPlanSubida {
  desde: number; hacia: number | "ilimitado"; via: "verificacion" | "entregas" | "uso" | string;
  porDia: number; dias: number; personas: number; alcanza: boolean;
  /** Contactos con consentimiento que faltan para este paso (0 si alcanza). */
  faltan: number;
}
export interface EstadoCalentamiento {
  /** limite null + ilimitado false = no hay número o no se entendió el límite. `mitad` = personas por día para que Meta suba. */
  nivel: { limite: number | null; ilimitado: boolean; siguiente: number | "ilimitado" | null; mitad: number | null };
  /** Los últimos 7 días (zona del negocio), del más viejo a hoy: personas distintas y si llegaron a la mitad. */
  dias: { dia: string; personas: number; cumple: boolean }[];
  diasQueCumplen: number;
  motivo: string;
  pasos: PasoCalentamiento[];
  /** Hasta dónde sube con la base de hoy, un nivel por vez. Opcional: un backend anterior no lo manda. */
  plan?: PasoPlanSubida[];
  hayNumero: boolean;
  calidad: string;
  /** «Que te escriban primero»: el link de wa.me y su QR (data URL), null sin número. */
  entrada: {
    link: string | null; qr: string | null; texto: string; textoSugerido: string;
    /** El texto guardado como consentimiento y si quien escriba con `texto` queda en la Base con su «sí». Opcionales: un
     *  backend anterior no los manda (y entonces no se dice nada del consentimiento). */
    guardado?: string; valeComoConsentimiento?: boolean;
  };
  actualizadoAt: string | null;
  /** Los días de compra que hacen «caliente» a un contacto. Opcional: un backend anterior no lo manda. */
  diasCaliente?: number;
  /** El freno por calidad de ahora. Opcional: un backend anterior no lo manda. */
  freno?: { activo: boolean; nivel: "ninguno" | "mitad" | "pausa"; hasta: string | null };
  /** Los cambios de nivel y de calidad de los últimos 90 días, del más nuevo al más viejo. Opcional, como `freno`. */
  historial?: EventoNumero[];
  /** De dónde sale el nivel: Twilio, el que cargó el equipo desde WhatsApp Manager (con su fecha) o el mínimo. Opcional, como `freno`. */
  origenLimite?: { fuente: LimiteMetaPublico["fuente"]; cargadoAt: string | null };
}
/** Un cambio del número: `mejora` true = subió de nivel o de calidad; null = la primera lectura. */
export interface EventoNumero { at: string; tipo: "inicio" | "nivel" | "calidad" | string; mejora: boolean | null; texto: string }

export interface GrupoAudiencia {
  id: string; nombre: string; emoji: string; personas: number; conCelular: number; optInTrue: number;
  /** Dijeron que sí Y tienen celular: a cuántos puede llegar WhatsApp como mucho. Opcional: un backend anterior no
   *  lo manda (ver `optInConCelularDe`). */
  optInConCelular?: number;
}
export interface AudienciaActiva {
  id: string; nombre: string; periodo: string; createdAt: string; grupos: GrupoAudiencia[];
  /** El comercio y el total de clientes del análisis: con la hora, lo distinguen de otro del mismo mes (que se llama
   *  igual). Opcionales: un backend anterior no los manda (ver `etiquetaAnalisis`). */
  marca?: string; clientes?: number;
  /** false = la marca todavía no eligió qué ofrecer (se elige en Reactivación): un mensaje con la «Oferta» no sale
   *  (OFERTA_SIN_ELEGIR). Opcional: un backend anterior no lo manda, y sin el dato no se avisa nada. */
  ofertaElegida?: boolean;
  /** Preparada en Nueva campaña con el motor (fase 4): el grupo de control ya quedó afuera. Va primero en la lista.
   *  Opcional: un backend anterior no lo manda. */
  motor?: boolean;
  segmentacionId?: string | null;
}
/**
 * Una campaña de Activa (Nueva campaña) ya preparada con todos sus grupos por mail (`GET /audiencias/sin-whatsapp`):
 * no está en `audiencias`, porque al preparar sólo los grupos de WhatsApp arman la campaña del oficial. El paso
 * Audiencia la nombra y dice cómo pasarla.
 */
export interface CampanaSinWhatsapp {
  segmentacionId: string;
  nombre: string;
  preparadaAt: string | null;
  /** los grupos que van, todos por mail */
  grupos: string[];
}
/**
 * Hasta cuántos de un análisis de Reactivación pueden recibir por WhatsApp oficial (`GET /audiencias/:id/alcance`): las
 * compuertas de la persona del chequeo (celular que sirve y consentimiento) y la fatiga, con la config de la marca. Es
 * un techo: al confirmar se descuentan además las bajas, los repetidos y a quien le falte una variable.
 */
export interface AlcanceAnalisis {
  campanaActivaId: string;
  /** el dataset de ejemplo: se cuenta, pero no se manda por WhatsApp oficial */
  esEjemplo: boolean;
  /** todos los contactos del análisis */
  clientes: number;
  /** ya sin los de la fatiga */
  pueden: number;
  /**
   * La fatiga de la marca (`dias`, 0 = sin fatiga): a cuántos de los que pasan las compuertas de la persona se les
   * escribió en esos días; no suman en `pueden`. Opcional: un backend anterior no la manda (ni la descuenta).
   */
  fatiga?: { dias: number; maximo?: number; excluidos: number };
  /** lo que dice el archivo, sobre todos: sin columna de consentimiento, `sinDato` son todos */
  consentimiento: { si: number; no: number; sinDato: number };
  /** de los que dijeron que sí, por qué no les llega: suman `consentimiento.si - pueden` */
  celularDeLosQueSi: Record<"sin_celular" | "telefono_invalido" | "numero_eeuu" | "telefono_ambiguo", number>;
  /** la config de la marca: un celular que vino sin +54 queda afuera */
  excluirAmbiguos: boolean;
  grupos: { id: string; pueden: number }[];
  /**
   * El análisis repartido entre las líneas por QR y este canal (API): los números de arriba son sólo los asignados a la
   * API. Opcional: un backend anterior no lo manda.
   */
  reparto?: { qr: number; api: number } | null;
  /** la cuenta está verificada: sin ella no sale nada por este canal. Opcional: un backend anterior no lo manda. */
  cuentaLista?: boolean;
  /**
   * Sólo en una campaña preparada en Nueva campaña: cómo quedó frente al consentimiento de la base. El sí se fija al
   * preparar, así que declararlo después no la cambia hasta prepararla de nuevo (26/10/2026).
   */
  preparacion?: {
    segmentacionId: string;
    preparadaAt: string | null;
    consentimientoDeclaradoAt: string | null;
    /** se declaró después de preparar, sobre la misma base */
    declaradoDespues: boolean;
    /** la base en uso ya no es la de la preparación: hay que segmentar de nuevo */
    baseCambio: boolean;
  };
  /**
   * Con listas de exclusión que valen para todas las campañas (29/10/2026) o con las exclusiones elegidas en el paso
   * Audiencia (`alcanceAnalisisConExcluir`): cuántos saca cada cosa. `pueden` y `grupos` ya los descuentan; `sacan`:
   * cuántos de los que podían recibir quedan afuera por ellas.
   */
  exclusiones?: ResumenExclusiones & { sacan: number };
}
export type FuenteOptIn = "csv" | "formulario" | "compra" | "declaracion_marca" | "desconocido";
export interface Consentimiento {
  /** Todos los contactos del análisis, también los que no tienen celular ni mail. */
  campanaActivaId: string; nombre: string; optInTrue: number; optInNull: number; optInFalse: number;
  porFuente: Partial<Record<FuenteOptIn, number>>;
  /** Opcionales: un backend anterior no los manda (ver `etiquetaAnalisis`). */
  marca?: string; createdAt?: string;
  /** Opcional (un backend anterior no lo manda): los contactos del análisis, con y sin celular. */
  clientes?: number;
  /** Opcional (un backend anterior no lo manda): los mismos tres, contados sólo entre los que tienen celular, los
   *  únicos a los que el oficial les puede escribir. */
  conCelular?: { total: number; optInTrue: number; optInNull: number; optInFalse: number; porFuente: Partial<Record<FuenteOptIn, number>> };
}
export interface ConstanciaContacto {
  destino: string;                                                     // siempre enmascarado
  consentimientos: { campanaActivaId: string; nombre: string; optIn: boolean | null; optInFuente: FuenteOptIn; optInFecha: string | null }[];
  /** `simulado` en las dos listas: vino del modo simulado y no salió a WhatsApp. Opcional: un backend anterior no lo manda. */
  bajas: { canal: string; motivo: string; createdAt: string; activo: boolean; simulado?: boolean }[];
  mensajes: { campanaId: string; campanaNombre: string; estado: EstadoMensajeOficial; aceptadoAt: string | null; baja: boolean; simulado?: boolean }[];
  /** Lo que se le mandó a mano desde Conversaciones (texto libre o plantilla, también marketing). Opcional: un backend
   *  anterior no lo manda. */
  enviosConversaciones?: {
    tipo: "texto" | "plantilla"; plantillaNombre: string; categoria: CategoriaCobro; estado: EstadoMensajeOficial;
    aceptadoAt: string | null; autor: { tipo: string; nombre: string }; simulado: boolean;
  }[];
  /** El contacto en la base de datos (SPEC-BD §4.5). Opcional: un backend anterior no lo manda; null = no está en la base. */
  base?: ConstanciaBase | null;
}
/** Lo que la base sabe del consentimiento de ese teléfono: la constancia del art. 5 (las 10 últimas entradas del historial). */
export interface ConstanciaBase {
  contactoId: string; optIn: boolean | null; fuente: "archivo_columna" | "archivo_declaracion" | "manual" | ""; detalle: string; fecha: string | null;
  cargas: { nombre: string; fecha: string }[];
  historial: { optIn: boolean | null; fuente: "archivo_columna" | "archivo_declaracion" | "manual"; detalle: string; fecha: string | null;
    carga: { id: string; nombre: string } | null; motivo: string; por: string; at: string }[];
}

export interface CostoEstimado {
  /** `maximoUSD` y `esperadoUSD` son lo que le cuesta a Deenex: no le llegan a una persona de un cliente. */
  maximoUSD?: number; esperadoUSD?: number; clienteMaximoUSD: number; clienteEsperadoUSD: number;
  porMensajeUSD?: number; tarifaVigencia: string; categoria: CategoriaCobro;
}
/**
 * Una tanda por día de la marca: `fecha` es el primer envío y `hasta` el último (un backend anterior no manda `hasta`
 * y su `fecha` era la apertura de la franja, no la hora real). `porDia`, `sinSalir`, `antes` y `porSegundo`, del plan
 * que simula el motor: lo que sale el día que más sale, lo que no llega antes de la fecha de fin, lo de las campañas
 * en cola que sale antes y la velocidad con que se calculó.
 */
export interface PlanTandas {
  tandas: { fecha: string; cantidad: number; hasta?: string }[]; dias: number; finEstimado: string | null;
  porDia?: number; sinSalir?: number; antes?: number; porSegundo?: number;
}
export interface Chequeo {
  calculadoAt: string; total: number; excluidos: Record<MotivoExclusion, number>;
  incluidos: number; envio: number;   // sin grupo de control, `envio` === `incluidos`
  variablesSinResolver: { variable: string; campo: CampoVariable; contactos: number }[];
  cupo24h: Cupo24h;
  plan: PlanTandas; costo: CostoEstimado;
  /** `comprometidoUSD`: lo que todavía van a gastar las campañas que salen antes (un backend viejo no lo manda) */
  saldo: { saldoUSD: number; comprometidoUSD?: number; alcanza: boolean; faltanteUSD: number };
  tope: { topeMensualUSD: number; gastoMesUSD: number; alcanza: boolean } | null;
  franjas: string; enFranjaAhora: boolean;
  enFranjaCuando?: boolean;              // sólo si se pidió con `cuando`
  proximaApertura: string | null;        // ISO; con `cuando` fuera de franja, la próxima apertura desde esa hora
  horario?: HorarioCampana | null;       // el horario de la campaña con el que se calculó (`franjas` ya es la intersección)
  bloqueos: CodigoBloqueo[];
  advertencias: { codigo: string; mensaje: string }[];
  huella: string;
  /** De los que reciben, cuántos son calientes y salen primero. Opcional: ausente si no se pudo saber o un backend anterior. */
  calientes?: { cantidad: number; diasCaliente: number };
  /** sólo con exclusiones en la audiencia: cuántos saca cada lista y cada campaña elegida */
  exclusiones?: ResumenExclusiones;
  /** sólo con el bloqueo GRUPOS_EN_OTRA_CAMPANA: qué otra campaña de la segmentación tiene esos grupos, en palabras */
  gruposEnOtraCampana?: string;
}
/** Cómo se dan de baja quienes reciben la campaña (paso «Horario y baja», 26/10): el botón de la plantilla, el texto del pie, los dos o ninguno. */
export interface BajaCampana { boton: boolean; texto: boolean }
export type CodigoBloqueo = "VARIABLES_SIN_RESOLVER" | "FALTA_IMAGEN" | "NADA_QUE_ENVIAR" | "SALDO_INSUFICIENTE" | "TOPE_MENSUAL"
  // el paso «Horario y baja»: falta elegirlos, o la baja elegida no está en la plantilla
  | "FALTA_HORARIO" | "FALTA_BAJA" | "BAJA_SIN_BOTON" | "BAJA_SIN_TEXTO"
  // con su horario no sale nadie antes de la fecha de fin (29/10/2026): antes era sólo el aviso «horario_no_alcanza»
  | "HORARIO_NO_ALCANZA"
  | "CUENTA_NO_VERIFICADA" | "SIN_WEBHOOKS" | "SIN_PUBLIC_BASE_URL" | "MARCA_FRENADA" | "MARCA_DEMO" | "SENDER_NO_ONLINE"
  | "PLANTILLA_NO_APROBADA"   // en revisión: el chequeo y la previa funcionan; la prueba y el confirmar no (SPEC-BD C2)
  | "GRUPOS_EN_OTRA_CAMPANA"  // un grupo ya está en otra campaña de la misma segmentación que salió o está saliendo
  // la plantilla aprobada no existe en la cuenta de Twilio conectada (A1-4, A1-6)
  | MotivoPlantillaInutilizable;
/** Por qué una plantilla APROBADA no sirve con la cuenta conectada (espejo de `uso-plantilla.util.ts`). */
export type MotivoPlantillaInutilizable = "PLANTILLA_SIMULADA" | "PLANTILLA_DE_OTRA_CUENTA";

// Sin `controlPct`: WhatsApp oficial no tiene grupo de control, ni con Activa ni con la base.
export interface CampanaEntrada {
  nombre?: string; plantillaId?: string;
  audiencia?: AudienciaEntrada;
  mapeo?: MapeoVariables;
  /** el paso «Horario y baja»: el horario (null = el de la marca) y cómo se dan de baja */
  horario?: HorarioCampana | null;
  baja?: BajaCampana;
}
/**
 * Activa: grupos [] = TODOS los grupos (SPEC-backend §5.3): nunca mandar [] desde «Elegir grupos». Sin `tipo` es Activa,
 * como antes. Base: sólo el público.
 */
export type AudienciaEntrada =
  | { tipo?: "activa"; campanaActivaId: string; grupos: string[]; excluir?: ExcluirAudiencia | null }
  | { tipo: "base"; publicoId: string; filtroCompras?: FiltroCompras | null; excluir?: ExcluirAudiencia | null }
  | { tipo: "publico"; filtroPublico: FiltroAudienciaPublico; filtroCompras?: FiltroCompras | null; excluir?: ExcluirAudiencia | null };
/**
 * Las exclusiones de la audiencia (28/10/2026): las campañas del oficial cuyos destinatarios se sacan (a quien le llegó
 * alguna) y las listas de exclusión de la marca. Los ids, en el orden en que se eligieron.
 */
export interface ExcluirAudiencia { campanas: string[]; listas: string[] }
/**
 * Lo que el conteo (y el chequeo) dicen de las exclusiones: a cuántos miró (los que dejó el filtro por compras), cuántos
 * saca en total (`enListas` + `porCampanas`, cada uno una vez) y a cuántos alcanza cada lista y cada campaña por sí sola.
 */
export interface ResumenExclusiones {
  personas: number; fuera: number; enListas: number; porCampanas: number;
  /** `siempre`: vale para todas las campañas (no se eligió en ésta); van primero */
  listas: { id: string; nombre: string; personas: number; siempre?: true }[];
  campanas: { id: string; nombre: string; personas: number }[];
}
/** `GET /exclusiones/campanas`: una campaña del oficial que le llegó a alguien (`fecha`: cuándo salió, ISO). */
export interface CampanaParaExcluir { id: string; nombre: string; estado: EstadoCampanaOficial; fecha: string | null; recibieron: number }
/**
 * Una lista de exclusión de la marca (`GET /exclusiones/listas`): sin sus celulares. `siempre` (29/10/2026): vale para todas
 * las campañas de la marca (se aplica sin elegirla); apagada, se elige en el bloque «Excluir» de cada campaña.
 */
export interface ListaExclusion { id: string; nombre: string; celulares: number; mails: number; siempre: boolean; creadaPor: string; createdAt: string | null; updatedAt: string | null }
/** Una lista para editarla: lo que tiene y en qué borradores está elegida. */
export interface ListaExclusionDetalle extends ListaExclusion {
  entradas: { celulares: string[]; mails: string[] };
  usadaEn: { id: string; nombre: string }[];
}
/** Lo que devuelven crear y editar una lista: la lista y lo que no entró de lo pegado o del archivo. */
export interface ListaExclusionGuardada { lista: ListaExclusionDetalle; invalidas: number; ejemplosInvalidas: string[] }
/** Crear o editar una lista: el nombre, lo pegado (reemplaza) y/o un archivo (con `agregar`, se suma). */
export interface EntradaListaExclusion {
  nombre?: string; texto?: string;
  nombreArchivo?: string; archivo?: { content: string; encoding: "csv" | "base64" }; agregar?: boolean;
  /** «Vale para todas las campañas»: sin el dato, una lista nueva vale siempre y una que se edita queda como estaba */
  siempre?: boolean;
}
/**
 * El filtro «Por compras» de la audiencia (28/10/2026), contra la base de ventas en uso: la última compra hace más / menos
 * de N días (en la zona del comercio), cuántas veces compró, el ticket promedio en pesos y la sucursal. null en un número
 * = esa condición no se usa. `sinBase`: qué pasa con quien no está en la base de ventas (por defecto, queda).
 */
export interface FiltroCompras {
  ultimaHaceMasDe: number | null; ultimaHaceMenosDe: number | null;
  comprasMin: number | null; comprasMax: number | null;
  ticketMin: number | null; ticketMax: number | null;
  sucursales: string[];
  sinBase: "dejar" | "sacar";
}
/** Lo que el conteo dice del filtro: a cuántos miró, cuántos están en la base de ventas y cuántos sacó. */
export interface ResumenCompras { personas: number; enLaBase: number; fuera: number }
/** `GET /audiencias/compras`: la base de ventas en uso con la que se filtra (null = no hay: el bloque pide subirla). */
export interface OpcionesCompras {
  base: { versionId: string; numero: number; desde: string | null; hasta: string | null; clientes: number; sucursales: string[] } | null;
}
/** El Público de Activa como audiencia: el segmento (id del grupo, «sin», o vacío = todos) y la actividad (vacía = cualquiera). */
/** `lista`: sólo las personas de un archivo subido en «Subir un Excel» (`ListaPublico`); sin ella, todo el Público del filtro. */
export interface FiltroAudienciaPublico { segmento: string; actividad: string; lista?: string }
/**
 * `POST /audiencias/publico/listas`: un Excel o un CSV cruzado con el Público (27/10/2026). Las columnas de celular,
 * mail e id del cliente las reconoce el backend; lo que no es de nadie del Público se puede cargar a la Base.
 */
export interface ListaPublico {
  id: string;
  nombre: string;
  nombreArchivo: string;
  columnas: { telefono: string[]; email: string[]; id: string[] };
  filas: number;
  sinDato: number;
  filasEnElPublico: number;
  personasEnElPublico: number;
  filasQueNoEstan: number;
  createdAt: string | null;
}
/** `GET /audiencias/publico`: los segmentos del Público y, con el filtro, cuántos pueden recibir y por qué no los demás. */
export interface AlcancePublico {
  filtro: FiltroAudienciaPublico;
  publico: { personas: number; conWhatsApp: number; segmentos: { id: string; nombre: string; emoji: string; personas: number }[]; armadoAt: string | null };
  /** las del filtro con WhatsApp, sin las que pidieron la baja */
  personas: number;
  pueden: number;
  /** `maximo`: K de la regla de la marca (un backend anterior no lo manda: 1) */
  fatiga: { dias: number; maximo?: number; excluidos: number };
  /** lo que dice la Base del oficial: quien no está en la Base cuenta como sin dato */
  consentimiento: { si: number; no: number; sinDato: number };
  /** dijeron que sí en la base de ventas o en un análisis, pero no están en la Base con un sí */
  siFueraDeLaBase: number;
  bajas: number;
  /** con el filtro «Por compras», también `filtro_compras` */
  excluidos: Partial<Record<string, number>>;
  excluirAmbiguos: boolean;
  /** sólo con el filtro «Por compras» (`GET /audiencias/compras/publico`) */
  compras?: ResumenCompras;
  /** sólo con exclusiones (`?excluir=`) */
  exclusiones?: ResumenExclusiones;
}
/** Lista cerrada (SPEC-backend §4): nunca llega un código numérico de Twilio. */
export type CodigoCorte = "bajas_3pct" | "sin_callbacks" | "plantilla_pausada" | "plantilla_deshabilitada"
  | "plantilla_no_aprobada" | "plantilla_de_otra_cuenta" | "plantilla_simulada" | "sender_offline" | "marca_frenada"
  | "cuenta_no_lista" | "saldo" | "tope_mensual" | "programacion_vencida" | "twilio_sin_respuesta"
  // se confirmó en otro modo que el del servidor (en simulado, con el servidor ya en twilio): no sale nunca
  | "modo_distinto";
export interface MotivoCorte { codigo: CodigoCorte; detalle: string; at: string }
export interface AlertaCampana { codigo: string; nivel: "info" | "alerta" | "corte"; mensaje: string; at: string }
export interface Contadores { total: number; enviados: number; entregados: number; leidos: number; fallidos: number; enCola: number }
export interface CampanaResumen {
  id: string; nombre: string; estado: EstadoCampanaOficial; espera: EsperaCampana; motivoCorte: MotivoCorte | null;
  /** `estado` deja que el asistente siga con una plantilla en revisión y frene con una rechazada (SPEC-BD C1-C2, B-10). */
  plantilla: { id: string; nombreVisible: string; categoria: string; estado: EstadoPlantilla; rejectionReason?: string } | null;
  /** null = incompleta (activa sin campaña de Activa, base sin público). `nombre`: el de la campaña de Activa o el del público. */
  audiencia: AudienciaResumen | null;
  programadaPara?: string | null; confirmadaAt?: string | null; iniciadaAt?: string | null; finalizadaAt?: string | null;
  /** su franja, su tope por día y su fecha de fin (null o ausente = los de la marca) */
  horario?: HorarioCampana | null;
  /** si ya se eligió el horario en el paso «Horario y baja» (false = una campaña nueva que todavía no). Opcional: un backend anterior no lo manda. */
  horarioElegido?: boolean;
  /** cómo se dan de baja quienes la reciben: null = falta elegirla; ausente = una campaña anterior a la elección */
  baja?: BajaCampana | null;
  contadores: Contadores; costoEstimado?: CostoEstimado | null; simulado: boolean; createdAt: string;
  /** algún mensaje salió o pudo haber salido: una cancelada con esto ya le escribió a alguien. Opcional: un backend anterior no lo manda. */
  salio?: boolean;
  /**
   * Se confirmó en otro modo que el del servidor (C1): con la cuenta real no sale nunca, y el backend
   * no deja reanudarla. Opcional: un backend anterior no lo manda (entonces se lee del corte).
   */
  otroModo?: boolean;
  /** sólo en un seguimiento (28/10/2026): de cuál campaña es (con su nombre) y el criterio, en palabras en `texto` */
  seguimientoDe?: SeguimientoDe & { nombre: string; texto: string };
  /** sólo en una campaña que tiene su seguimiento: cuál es (el tope es uno por campaña) */
  seguimiento?: { campanaId: string; nombre: string; estado: EstadoCampanaOficial | null };
  /** sólo en una campaña que se repite (28/10/2026): la regla (en palabras en `texto`), la próxima y cómo quedó la última */
  repeticion?: ResumenRepeticion;
  /** sólo en una repetición: de cuál campaña es, cuál de la serie y si salió sola o quedó en borrador (con sus motivos) */
  repeticionDe?: {
    campanaId: string; nombre: string; n: number; programadaPara: string;
    /** en la que se lee `programadaPara` (la de la regla); un backend anterior no la manda */
    zona?: string;
    decision: DecisionRepeticion | null; motivos: string[];
  };
  /** sólo en una que armó un disparador (29/10/2026): cuál, qué día y con qué base de ventas (el asistente muestra el atraso) */
  disparadorDe?: DisparadorDeLaCampana;
}
export interface AudienciaResumen {
  tipo: "activa" | "base" | "publico"; campanaActivaId: string; nombre: string; grupos: string[];
  publicoId: string; publicoTipo: "dinamico" | "estatico" | null;
  /** sólo en una audiencia del Público de Activa */
  filtroPublico?: FiltroAudienciaPublico;
  /** el filtro «Por compras» (Público y Base): sólo si la campaña filtra */
  filtroCompras?: FiltroCompras;
  /** las exclusiones elegidas (con cualquier audiencia desde el 29/10/2026), con los nombres de ahora: sólo si la campaña excluye algo. `borrada`: ya no se aplica */
  excluir?: {
    campanas: { id: string; nombre: string; existe: boolean }[];
    listas: { id: string; nombre: string; borrada: boolean }[];
  };
}
export interface Embudo {
  enviados: number; entregados: number; leidos: number; respondieron: number; bajas: number; fallidos: number;
  enCola: number; desconocidos: number; sinConfirmacion: number; excluidosAlDespachar: Partial<Record<MotivoExclusion, number>>;
  reintentosProgramados: number; programadosParaManana: number;
}
export interface CampanaDetalle extends CampanaResumen {
  mapeo: MapeoVariables; chequeo: Chequeo | null; alertas: AlertaCampana[];
  embudo: Embudo; ultimasRespuestas: RespuestaEntrante[];
  /** Quién del equipo hizo qué (mail e IP): no le llega a una persona de un cliente (el backend lo saca). */
  auditoria?: { accion: string; por: Quien; at: string; detalle?: unknown }[];
  puedeEditar: boolean; puedeConfirmar: boolean; puedePausar: boolean; puedeReanudar: boolean; puedeCancelar: boolean;
  esperaHasta: string | null;            // cuándo sigue de verdad, según el plan (la franja, el cupo, su tope y las que salen antes); si no, null
  /** lo de la cola que ya no sale antes de su fecha de fin (se cancela a las 00:00); un backend viejo no lo manda */
  sinSalirAlFin?: number;
  franjasTexto: string;
  /** ya mandó, no es un seguimiento, no tiene el suyo y es del modo de hoy: se ofrece «Hacer un seguimiento». Un backend viejo no lo manda. */
  puedeHacerSeguimiento?: boolean;
  /** se confirmó, no es un seguimiento ni una repetición y es del modo de hoy: se ofrece «Repetir esta campaña» (al equipo) */
  puedeRepetir?: boolean;
  /** sólo en una campaña que se repite: sus repeticiones, de la más nueva a la más vieja */
  repeticiones?: FilaRepeticion[];
  /**
   * Sólo en una repetición en borrador (29/10/2026): la última prueba que llegó en la original o en otra repetición de la
   * serie, con la plantilla de ésta (null si no hay). Un dato para decidir «Seguir sin probar»: no vale como la de ésta.
   * Un backend anterior no lo manda.
   */
  pruebaDeLaSerie?: { campanaId: string; nombre: string; plantillaId: string; at: string } | null;
}

/* ── Las repeticiones de una campaña (28/10/2026) ── */

export type CadaRepeticion = "semana" | "2semanas" | "mes";
export type EstadoRepeticion = "activa" | "pausada" | "terminada";
/** «decidiendo» es un instante (el programador la está chequeando); «salio» = se confirmó sola. */
export type DecisionRepeticion = "decidiendo" | "salio" | "borrador";
/** Lo que se elige en «Repetir esta campaña». `dia`: 0 (domingo) a 6 cada semana o cada 2; 1 a 31 cada mes. */
export interface ReglaRepeticion {
  cada: CadaRepeticion; dia: number; hora: string; zona: string;
  /** hasta una fecha («AAAA-MM-DD») o `veces`: una de las dos */
  hasta: string | null; veces: number | null;
  saleSola: boolean;
}
export interface ResumenRepeticion extends ReglaRepeticion {
  estado: EstadoRepeticion;
  /** por qué terminó sola («Se canceló la campaña original.»); null en las demás. Un backend anterior no lo manda. */
  motivoFin?: string | null;
  /** «Cada semana, los lunes a las 19:00 · 4 veces» */
  texto: string;
  n: number;
  proxima: string | null;
  ultima: {
    campanaId: string; nombre: string; estado: EstadoCampanaOficial | null; n: number; programadaPara: string;
    salio: boolean; motivos: string[];
  } | null;
}
export interface FilaRepeticion {
  id: string; nombre: string; n: number; programadaPara: string; estado: EstadoCampanaOficial;
  decision: DecisionRepeticion | null; motivos: string[];
}
/** `GET /campanas/:id/repeticion`: la página «Repetir esta campaña». */
export interface OpcionesDeRepeticion {
  campana: { id: string; nombre: string; estado: EstadoCampanaOficial };
  /** null = se puede repetir; si no, por qué */
  motivo: string | null;
  actual: ResumenRepeticion | null;
  sugerida: ReglaRepeticion;
  /** la pedida, validada; null si no se pidió o le falta algo (`error`) */
  regla: ReglaRepeticion | null;
  error: string | null;
  proximas: string[];
  advertencias: string[];
  anticipacionHoras: number;
}

/* ── El seguimiento de una campaña (28/10/2026) ── */

/** A quiénes de la original sigue: los que no la leyeron, los que no tocaron el link propio o los que no compraron. */
export type CriterioSeguimiento = "no_leyeron" | "no_tocaron" | "no_compraron";
export const CRITERIOS_SEGUIMIENTO: CriterioSeguimiento[] = ["no_leyeron", "no_tocaron", "no_compraron"];
/** Las opciones del seguimiento: `dias` sólo con «no compraron» (null en los otros). */
export interface OpcionesSeguimiento { criterio: CriterioSeguimiento; dias: number | null; sinBase: boolean; respondieron: boolean }
export interface SeguimientoDe extends OpcionesSeguimiento { campanaId: string }
export type DisponibilidadCriterio =
  | { disponible: true; cumplen: number; quedan: number; advertencias: string[] }
  | { disponible: false; mensaje: string; accion?: string };
/** `GET /campanas/:id/campana-de-seguimiento`: los números son de HOY (la hija los recalcula al chequear y al confirmar). */
export interface OpcionesDeSeguimiento {
  campana: { id: string; nombre: string; estado: EstadoCampanaOficial };
  seguimiento: { id: string; nombre: string } | null;
  /** null = se puede armar; si no, por qué */
  motivo: string | null;
  opciones: OpcionesSeguimiento;
  llegaron: number;
  criterios: Record<CriterioSeguimiento, DisponibilidadCriterio> | null;
  elegido: {
    cumplen: number;
    fuera: { bajas: number; respondieron: number };
    fueraDeLaBase: number;
    quedan: number;
    noEstan: number;
    /** los que pasan hoy las compuertas de cualquier campaña (como una promoción, sin mirar variables) */
    pueden: number;
    excluidos: Partial<Record<MotivoExclusion, number>>;
    fatigaDias: number;
    /** K de la regla de la marca (un backend anterior no lo manda: 1) */
    fatigaMaximo?: number;
    texto: string;
  } | null;
}
export interface EjemploPrevia { personaIdEnmascarado: string; nombre: string; destino: string;
  variables: Record<string, string>; texto: string; usoDefault: string[] }
/** `pie` y `encabezadoTexto` sólo vienen con `whatsapp/card`: ahí el pie NO está dentro de `texto` (D-A). */
export interface Previa { ejemplos: EjemploPrevia[]; plantilla: { tipo: TipoPlantilla; botones: BotonPlantilla[]; mediaUrl?: string; pie?: string; encabezadoTexto?: string } }
/** Un rechazo de Twilio llega con 200 y estado "fallido"; un timeout con 200 y "desconocido" (SPEC-backend §9.4.6). */
export interface ResultadoPrueba { mensajeId: string; estado: EstadoMensajeOficial; messageSid?: string | null; costoReservadoUSD: number;
  texto: string; destino: string; codigoError: string | null; motivoError: string | null; simulado: boolean }
export interface EstadoPrueba { estado: EstadoMensajeOficial; codigoError: string | null; motivoError: string | null;
  entregadoAt: string | null; leidoAt: string | null; costoClienteUSD: number | null; simulado: boolean }
export interface ResultadoConfirmar { estado: EstadoCampanaOficial; programadaPara: string | null; encolados: number;
  plan: PlanTandas; costoEstimado: CostoEstimado }
export interface MensajeFila {
  id: string; personaId: string; grupoId: string; destino: string; estado: EstadoMensajeOficial;
  codigoError?: string | null; motivoError?: string | null; motivoExclusion?: MotivoExclusion | null; intento: number;
  aceptadoAt?: string | null; entregadoAt?: string | null; leidoAt?: string | null;
  respondio: boolean; baja: boolean; costoClienteUSD: number; simulado: boolean;
  /** Envíos que se cobraron (un «venció en la cola» se reintenta hasta 3 veces y el costo los suma).
   *  Opcional: un backend anterior no lo manda. */
  envios?: number;
}
export interface PaginaMensajes { items: MensajeFila[]; total: number; page: number; limit: number; paginas: number; enmascarado: boolean }
export interface RespuestaEntrante {
  id: string; destino: string; texto: string; buttonPayload?: string; esBaja: boolean; autoRespondida: boolean;
  atribucion: "respuesta_a_mensaje" | "ultimo_envio_7d" | "sin_atribuir"; createdAt: string; campanaId?: string;
  /** La escribió el simulador: nadie la mandó por WhatsApp (C4). Opcional: un backend anterior no lo manda. */
  simulado?: boolean;
  /**
   * Qué mandó (foto, audio, ubicación…) y su conversación; null si todavía no está atada a una. Opcionales: un
   * backend anterior no los manda.
   */
  tipo?: TipoEntrante;
  conversacionId?: string | null;
}
export interface PaginaRespuestas { items: RespuestaEntrante[]; total: number; page: number; limit: number }
/** `exento` es por código; `estado` separa `fallido` de `no_entregado` (sólo al `fallido` se le exime el cargo). */
export interface FallidoPorCodigo { codigo: string; cantidad: number; titulo: string; accionTexto: string; exento: boolean; estado?: "fallido" | "no_entregado" }
export interface Resultado {
  embudo: Embudo; tasas: { entrega: number; lectura: number; respuesta: number; bajas: number };
  fallidosPorCodigo: FallidoPorCodigo[];
  /** `realUSD` (lo que pagó Deenex) no le llega a una persona de un cliente: el backend lo saca de su respuesta. */
  costoReal: { realUSD?: number; clienteUSD: number; estimadoClienteUSD: number; desvioPct: number };
  leidoAclaracion: string; medicion: Medicion | null; hayDatosSimulados: boolean;
}
export interface GrupoMedido { n: number; volvieron: number; tasa: number; plata: number; sinBaseComparable?: number }
/**
 * La recompra contra la base de ventas en uso (una campaña a un público de la Base o al Público de Activa). `tasa` es
 * sobre `enLaBase`: de los que no están en la base no se puede saber si compraron. `plata` en pesos.
 */
export interface GrupoVentas extends GrupoMedido { enLaBase: number; pedidos: number; usaronCupon: number }
/** Por dónde va la ventana de la medición (`estadoDeLaCampana` del backend). */
export type EstadoVentana = "preparada" | "enviando" | "midiendo" | "esperando_base" | "terminada";
export interface MedicionVentas {
  disponible: true; atribucion: "base-de-ventas"; incremental: false;
  envio: GrupoVentas; entregados: GrupoVentas;
  /** los códigos que salieron en la variable «Código de cupón» de la plantilla; null si no lleva */
  cupon: { codigos: string[] } | null;
  /** la base con la que se midió, «AAAA-MM-DDTHH:MM» de pared */
  base: { versionId: string; desde: string | null; hasta: string | null };
  ventana: { dias: number; primerEnvio: string | null; midiendoHasta: string | null; estado: EstadoVentana; completa: boolean };
  advertencias: string[]; medidoAt: string;
}
/** Sin grupo de control: cuenta quiénes volvieron a comprar, NO el efecto del mensaje (`incremental: false`).
 *  `entregados` son «los que recibieron»; `envio`, todos los asignados al confirmar (con la base de ventas: los que
 *  salieron). `accion`: qué hacer, para quien puede subir la base de ventas. `EN_ENVIO`: con la base de ventas,
 *  `/resultado` no la calcula mientras la campaña está programada o enviando («Se mide cuando termina el envío»). */
export type Medicion =
  | { disponible: true; atribucion: "exacta-por-snapshot" | "pendiente-base-nueva"; incremental: false;
      envio: GrupoMedido; entregados: GrupoMedido; advertencias: string[]; medidoAt: string }
  | MedicionVentas
  | { disponible: false; motivo: "SIMULADO" | "SIN_BASE_POSTERIOR" | "SIN_ENVIOS" | "EN_ENVIO" | "SIN_BASE_DE_VENTAS" | "BASE_DE_VENTAS_ANTERIOR" | string; mensaje: string; accion?: string };
export interface ArchivoCsv { content: string; encoding: "csv" | "base64" }
export interface SimularInboundResultado { procesado: boolean; esBaja: boolean; atribucion: RespuestaEntrante["atribucion"]; respuestaId: string }

// ── Conexión con Twilio: descubrir → conectar → diagnóstico (SPEC-estadisticas-conexion §A.4–§A.6) ──

export type CalidadNumero = "GREEN" | "YELLOW" | "RED" | "UNKNOWN";
/** Códigos de advertencia de «Buscar en Twilio» (§6.2). Llegan como string: uno nuevo se muestra tal cual. */
export type CodigoAdvertenciaDescubrimiento = "cuenta_trial" | "cuenta_inactiva" | "sin_numeros" | "sin_numero_operativo" | "muchos_servicios";

/** Lo que Twilio tiene en la cuenta. `POST /cuenta/descubrir` NO escribe nada (ni en Mongo ni en Twilio). */
export interface Descubrimiento {
  simulado: boolean;
  cuenta: { sid: string; nombre: string; estado: string; tipo: "Trial" | "Full" | string | null; esSubcuenta: boolean | null };
  saldo: { valor: number | null; moneda: string | null } | null;            // null si Balance falló o es subcuenta
  servicios: { sid: string; nombre: string; entradaUrl: string | null; entradaEsNuestra: boolean; numeros: string[] }[];
  numeros: {
    sid: string; numero: string; estado: string; operativo: boolean;
    calidad: CalidadNumero; calidadRaw: string;
    limite: number | null; ilimitado: boolean; limiteRaw: string; nombre: string; wabaId: string;
    servicios: string[]; motivosOffline: string[];
  }[];
  sugerencia: { messagingServiceSid: string | null; senderSid: string | null; crearServicio: boolean; motivo: string };
  advertencias: (CodigoAdvertenciaDescubrimiento | string)[];
  /** El Account SID ya está conectado a otro comercio: «Conectar» daría ACCOUNT_SID_EN_USO. Opcional: un backend
   *  anterior no lo manda (y entonces el 409 llega al conectar, como antes). */
  enOtroComercio?: boolean;
}
export interface DescubrirEntrada { accountSid: string; authToken?: string }
/** `messagingServiceSid` y `crearServicio` son excluyentes: uno de los dos, nunca ambos. */
export interface ConectarEntrada {
  accountSid: string; authToken?: string;
  messagingServiceSid?: string; crearServicio?: { nombre: string };
  senderSid: string; reemplazarEntrada?: boolean;
}
export type IdPasoConexion = "credenciales" | "cuenta" | "servicio" | "numero_en_servicio" | "verificar" | "entrada" | "event_streams" | "alertas_twilio";
export interface PasoConexion {
  id: IdPasoConexion | string;
  estado: "ok" | "error" | "omitido" | "requiere_confirmacion";
  titulo: string; detalle: string; codigoTwilio?: string; urlAnterior?: string;
}

export type IdPaso =
  | "servidor.modo" | "servidor.url_publica" | "servidor.alcanzable" | "servidor.clave"
  | "cuenta.credenciales" | "cuenta.estado" | "cuenta.tipo" | "cuenta.saldo_twilio"
  | "numero.servicio" | "numero.registrado" | "numero.estado" | "numero.calidad" | "numero.limite" | "numero.en_servicio"
  | "recepcion.entrada" | "recepcion.webhook_numero" | "recepcion.recibos" | "recepcion.event_streams" | "recepcion.prueba_twilio"
  | "recepcion.alertas_twilio"
  | "cumplimiento.opt_out" | "cumplimiento.compliance" | "cumplimiento.meta" | "cumplimiento.portfolio"
  | "costos.saldo_interno" | "costos.plantillas_otra_cuenta" | "costos.alertas_twilio";
export type EstadoPaso = "ok" | "advertencia" | "error" | "pendiente" | "no_aplica";
export type GrupoPaso = "servidor" | "cuenta" | "numero" | "recepcion" | "cumplimiento" | "costos";
export type ClaveConfirmacionManual = "advancedOptOutConfirmado" | "complianceConfirmado" | "portfolioVerificado";
export type AccionPaso =
  | { tipo: "autoconfigurar" }
  | { tipo: "consola"; url: string; etiqueta: string }
  | { tipo: "variable_entorno"; nombre: string }
  | { tipo: "config"; seccion: "cuenta" | "envio" | "bajas" | "saldo" }
  | { tipo: "confirmar_manual"; clave: ClaveConfirmacionManual }
  | { tipo: "rotar_token" }
  /** el freno por `cumplimiento` quedó superado por una prueba del Sandbox: reanudar la marca con el motivo cargado */
  | { tipo: "reanudar_marca" };
export interface PasoDiagnostico {
  id: IdPaso | string; grupo: GrupoPaso | string;
  titulo: string; estado: EstadoPaso; detalle: string; arreglo?: string; accion?: AccionPaso;
  codigoTwilio?: string; bloqueante: boolean;
}
export interface Diagnostico { at: string; modo: ModoTransporte; listoParaEnviar: boolean; bloqueantes: (IdPaso | string)[]; pasos: PasoDiagnostico[] }
export interface ResultadoConexion { cuenta: CuentaPublica; pasos: PasoConexion[]; diagnostico: Diagnostico }
export interface ResultadoAutoconfigurar { pasos: PasoConexion[]; diagnostico: Diagnostico }

// ── Restos del modo simulado al pasar a real (GET/POST /cuenta/restos-simulados) ──

export interface RestosSimulados {
  hayRestos: boolean;
  plantillas: { total: number; aprobadas: number };
  campanas: { abiertas: number; borradoresConPlantillaSimulada: number };
  mensajes: number;
  movimientos: { total: number; fueraDelSaldo: number };
  respuestas: number;
  /** Bajas activas con la marca `simulado: true`: las únicas que la limpieza levanta. */
  noContactarSimuladas: number;
  /**
   * Bajas activas del canal oficial SIN el campo `simulado` (de antes de la marca). La limpieza no las
   * toca nunca (Ley 25.326: en la duda, la baja se queda): sólo se avisan para revisarlas a mano.
   * Opcional: un backend anterior no lo manda.
   */
  noContactarSinMarca?: number;
  /** La cuenta todavía tiene datos del simulador (número, fechas, confirmaciones «Ya lo hice»). */
  cuentaConRestos: boolean;
  /** La marca quedó frenada por un código de un mensaje simulado: la limpieza la levanta. Opcional: un backend anterior no lo manda. */
  marcaFrenadaSimulada?: boolean;
  rebase: RebaseSaldo | null;
}
export interface ResultadoLimpiezaRestos {
  antes: RestosSimulados; despues: RestosSimulados;
  acciones: { plantillasArchivadas: number; campanasCanceladas: number; noContactarLevantadas: number; cuentaLimpia: boolean };
}

/** Estado de la «Prueba con el Sandbox» (§A.6): sirve para probar la conexión, no las campañas. */
export interface EstadoSandbox {
  urlEntrada: string | null; urlEstado: string | null;   // null si al servidor le falta PUBLIC_BASE_URL
  numeroSandbox: string;
  ultimaPruebaAt?: string | null; ultimoEstado: string; ultimoCodigoError: string;
  destinoEnmascarado: string; ultimoInboundAt?: string | null;
}
/** `frenoSuperado`: la marca está frenada por `cumplimiento` y esta prueba salió después del freno. Opcional. */
export interface ResultadoPruebaSandbox { messageSid: string | null; estado: string; frenoSuperado?: FrenoSuperado }

/* ── Imágenes de las plantillas (fase 2, `/activa/oficial/imagenes`) ─────────────────────────────── */

/** Una plantilla sin archivar que usa la imagen (por eso no se puede borrar). */
export interface UsoImagen { id: string; nombreVisible: string }
export interface ImagenOficial {
  id: string; nombre: string; tipo: "image/jpeg" | "image/png"; bytes: number;
  ancho: number | null; alto: number | null;
  /** El link https que va en la plantilla; `null` si el servidor no tiene PUBLIC_BASE_URL. */
  url: string | null;
  createdAt: string | null; usos: UsoImagen[];
}
/** `conLink: false`: el servidor no tiene PUBLIC_BASE_URL y ninguna imagen tiene link. */
export interface ListaImagenes { imagenes: ImagenOficial[]; conLink: boolean; maxBytes: number }
export interface ImagenSubida { imagen: ImagenOficial; yaEstaba: boolean }
